import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Copy, Loader2, Ban, CheckCircle2, Trash2 } from 'lucide-react';

const AdminInviteCodesPage = () => {
  const { api } = useAuth();
  const [codes, setCodes] = useState([]);
  const [waitlist, setWaitlist] = useState({ buckets: [], recent: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create form
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');
  const [planLabel, setPlanLabel] = useState('');
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, w] = await Promise.all([
        api.get('/admin/invite-codes'),
        api.get('/admin/pos-waitlist'),
      ]);
      setCodes(c.data.items || []);
      setWaitlist(w.data || { buckets: [], recent: [] });
    } catch (e) {
      toast.error('Could not load invite codes');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resetForm = () => {
    setCode(''); setLabel(''); setMaxUses(1); setExpiresAt(''); setPlanLabel(''); setNotes('');
  };

  const create = async () => {
    if (!code.trim()) return toast.error('Enter a code');
    setSaving(true);
    try {
      await api.post('/admin/invite-codes', {
        code: code.toUpperCase(),
        label: label || null,
        max_uses: Number(maxUses) || 1,
        expires_at: expiresAt || null,
        defaults: {
          plan_label: planLabel || null,
          notes: notes || null,
          comp_until: 'forever',
        },
      });
      toast.success('Code created');
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create code');
    } finally { setSaving(false); }
  };

  const toggleRevoke = async (c) => {
    try {
      await api.put(`/admin/invite-codes/${c.id}`, { revoked: !c.revoked });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete code ${c.code}?`)) return;
    try {
      await api.delete(`/admin/invite-codes/${c.id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const copyCode = (c) => {
    navigator.clipboard?.writeText(c);
    toast.success(`Copied ${c}`);
  };

  return (
    <div className="space-y-6 p-6" data-testid="admin-invite-codes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Invite Codes</h1>
          <p className="text-sm text-muted-foreground">Issue codes to new operators. Codes are consumed only when onboarding finishes.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-code-btn">
          <Plus className="w-4 h-4 mr-2" /> New code
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Codes</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : codes.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center">No codes yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2">Code</th>
                  <th>Label</th>
                  <th>Uses</th>
                  <th>Status</th>
                  <th>Used by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => (
                  <tr key={c.id} className="border-b last:border-0" data-testid={`code-row-${c.code}`}>
                    <td className="py-3 font-mono font-semibold flex items-center gap-2">
                      {c.code}
                      <button onClick={() => copyCode(c.code)} className="text-muted-foreground hover:text-foreground">
                        <Copy className="w-3 h-3" />
                      </button>
                    </td>
                    <td>{c.label || '—'}</td>
                    <td>{c.uses_count || 0} / {c.max_uses}</td>
                    <td>
                      {c.revoked ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : (c.uses_count >= c.max_uses) ? (
                        <Badge variant="secondary">Used up</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>
                      )}
                    </td>
                    <td className="text-xs text-muted-foreground">
                      {(c.used_by || []).map((u, i) => (
                        <div key={i}>{u.org_name || u.org_id?.slice(0, 8)} · {u.at?.slice(0, 10)}</div>
                      ))}
                    </td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleRevoke(c)}
                        data-testid={`toggle-revoke-${c.code}`}
                      >
                        {c.revoked ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                      {!c.uses_count && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => del(c)}
                          data-testid={`delete-${c.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>POS Waitlist</CardTitle></CardHeader>
        <CardContent>
          {waitlist.buckets.length === 0 ? (
            <p className="text-muted-foreground py-4 text-center">No waitlist requests yet.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {waitlist.buckets.map(b => (
                <div key={b.pos_name} className="px-4 py-3 bg-muted/40 rounded-lg">
                  <div className="font-semibold capitalize">{b.pos_name.replace('_', ' ')}</div>
                  <div className="text-sm text-muted-foreground">{b.count} request{b.count !== 1 ? 's' : ''}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-code-dialog">
          <DialogHeader><DialogTitle>New invite code</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Code</Label>
              <Input
                data-testid="new-code-value"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="PEEKAYUK"
              />
            </div>
            <div>
              <Label>Label (internal)</Label>
              <Input data-testid="new-code-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Peekay — pilot" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max uses</Label>
                <Input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
              </div>
              <div>
                <Label>Expires at (optional)</Label>
                <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value ? new Date(e.target.value).toISOString() : '')} />
              </div>
            </div>
            <div>
              <Label>Plan label (shown to user)</Label>
              <Input value={planLabel} onChange={(e) => setPlanLabel(e.target.value)} placeholder="Pilot — 90 days" />
            </div>
            <div>
              <Label>Welcome note (shown to user)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="You've been invited by Paul…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="create-code-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInviteCodesPage;
